/**
 * sportsMetrics.js
 *
 * Central registry of per-sport advanced scoring metrics.
 *
 * Structure
 * ─────────
 * SPORTS_METRICS[sportKey] = {
 *   positions: string[]          — player positions for this sport
 *   positionMetrics: { [pos]: string[] } — metric keys limited to a position (empty = all)
 *   categories: {
 *     [categoryKey]: {
 *       label: string
 *       icon:  string
 *       metrics: {
 *         [metricKey]: {
 *           label:        string   — display name
 *           description:  string   — tooltip / helper text
 *           affectsScore: boolean  — does recording this also increment the team score?
 *           scorePoints:  number   — points the score goes up by when affectsScore (default 1).
 *                                    The backend credits the same value to points_scored
 *                                    (backend/src/services/match/sportMetricCatalog.js).
 *           isPositive:   boolean  — good event (green) or negative (red/yellow)
 *           unit:         string   — 'count' | 'percent' | 'rating'
 *           positions:    string[] — if set, only shown for these positions; [] = all
 *           awardsOpponentPoint: boolean — a fault: the opponent wins the point (Opp+1)
 *           types:        { [typeKey]: { label, positions? } } — optional one-tap variants
 *                                    recorded as this metric plus `typeField: typeKey`
 *           typeField:    string   — event field the type is stored in (e.g. 'killType')
 *           legacy:       boolean  — kept so old player_stats rows still display; never a chip
 *         }
 *       }
 *     }
 *   }
 * }
 */

import { getSportConfigKey } from './sportsConfig';

export const SPORTS_METRICS = {
  // ── VOLLEYBALL ─────────────────────────────────────────────────────────────
  volleyball: {
    positions: ['Setter', 'Outside Hitter', 'Middle Blocker', 'Opposite Hitter', 'Libero', 'Defensive Specialist'],
    // Keys shown in the flat quick-tap grid during a live match (order matters)
    quickMetrics: ['kill', 'attackError', 'ace', 'serviceError', 'blockSolo', 'blockAssist', 'dig', 'assist', 'foul'],
    positionMetrics: {
      Libero:               ['digs', 'passAccuracy', 'fouls'],
      'Defensive Specialist': ['digs', 'passAccuracy', 'fouls'],
      Setter:               ['assists', 'passAccuracy', 'fouls', 'communication'],
    },
    categories: {
      offense: {
        label: 'Offense',
        icon: '⚔️',
        metrics: {
          kill:            { label: 'Kill',             description: 'Attack that directly results in a point',      affectsScore: true,  isPositive: true,  unit: 'count', positions: [],
                             typeField: 'killType',
                             types: { tip: { label: 'Tip' }, placementShot: { label: 'Placement' }, setterDump: { label: 'Setter Dump', positions: ['Setter'] } } },
          attackError:     { label: 'Attack Error',     description: 'Attack hit out, into the net or blocked down — opponent earns a point', affectsScore: false, isPositive: false, unit: 'count', positions: [], awardsOpponentPoint: true },
          attackAttempt:   { label: 'Attack Attempt',   description: 'Any offensive attack attempt',                 affectsScore: false, isPositive: true,  unit: 'count', positions: [] },
          attackSuccessPct:{ label: 'Attack Success %', description: 'Percentage of attacks that result in a kill',  affectsScore: false, isPositive: true,  unit: 'percent', positions: [] },
          setterDump:      { label: 'Setter Dump',      description: 'Setter attacks the ball directly over the net instead of setting, catching the defence off guard — scores a point', affectsScore: true, isPositive: true, unit: 'count', positions: ['Setter'], legacy: true },
          tip:             { label: 'Tip',              description: 'Soft open-hand tip over or around a block — scores a point', affectsScore: true, isPositive: true, unit: 'count', positions: [], legacy: true },
          placementShot:   { label: 'Placement Shot',   description: 'Precision shot aimed at an open area of the court — scores a point', affectsScore: true, isPositive: true, unit: 'count', positions: [], legacy: true },
        },
      },
      serve: {
        label: 'Serve',
        icon: '🏐',
        metrics: {
          ace:             { label: 'Ace',              description: 'Serve that lands untouched for a point',       affectsScore: true,  isPositive: true,  unit: 'count', positions: [] },
          serviceError:    { label: 'Service Error',    description: 'Serve fault — opponent earns a point',          affectsScore: false, isPositive: false, unit: 'count', positions: [], awardsOpponentPoint: true },
        },
      },
      defense: {
        label: 'Defense',
        icon: '🛡️',
        metrics: {
          dig:             { label: 'Dig',              description: 'Defensive pass keeping the ball in play',      affectsScore: false, isPositive: true,  unit: 'count', positions: [] },
          blockSolo:       { label: 'Block (Solo)',     description: 'Solo block that stops an opposing attack',     affectsScore: true,  isPositive: true,  unit: 'count', positions: ['Outside Hitter','Middle Blocker','Opposite Hitter'] },
          blockAssist:     { label: 'Block (Assist)',   description: 'Shared block — pick the partner; both blockers get an assist, the team one point', affectsScore: false, isPositive: true,  unit: 'count', positions: ['Outside Hitter','Middle Blocker','Opposite Hitter'] },
        },
      },
      playmaking: {
        label: 'Playmaking',
        icon: '🎯',
        metrics: {
          assist:          { label: 'Assist',           description: 'Set that directly leads to a kill',            affectsScore: false, isPositive: true,  unit: 'count', positions: ['Setter','Outside Hitter','Opposite Hitter'] },
          passAccuracy:    { label: 'Passing Accuracy %', description: 'Percentage of passes rated 3 or above',     affectsScore: false, isPositive: true,  unit: 'percent', positions: [] },
        },
      },
      discipline: {
        label: 'Discipline',
        icon: '⚠️',
        metrics: {
          foul:            { label: 'Foul / Error',     description: 'Rule violation or unforced error — opponent earns a point', affectsScore: false, isPositive: false, unit: 'count', positions: [], awardsOpponentPoint: true },
        },
      },
      team: {
        label: 'Team',
        icon: '👥',
        metrics: {
          communication:   { label: 'Communication',   description: 'Rating of communication effectiveness (1-5)',   affectsScore: false, isPositive: true,  unit: 'rating', positions: [] },
        },
      },
    },
  },

  // ── BASKETBALL ─────────────────────────────────────────────────────────────
  basketball: {
    positions: ['Point Guard', 'Shooting Guard', 'Small Forward', 'Power Forward', 'Center'],
    // Made shots carry their point value (FT 1, 2PT 2, 3PT 3); misses are stats only
    quickMetrics: ['freeThrowMade', 'twoPointerMade', 'threePointerMade', 'assist', 'steal', 'blockShot', 'offensiveRebound', 'defensiveRebound', 'turnover', 'foul', 'freeThrowMissed', 'twoPointerMissed', 'threePointerMissed'],
    positionMetrics: {
      Center: ['offensiveRebound', 'defensiveRebound', 'blockShot', 'fouls'],
    },
    categories: {
      offense: {
        label: 'Offense',
        icon: '🏀',
        metrics: {
          freeThrowMade:   { label: 'FT Made',          description: 'Free throw made (+1)',                         affectsScore: true,  isPositive: true,  scorePoints: 1, unit: 'count', positions: [] },
          twoPointerMade:  { label: '2PT Made',         description: 'Field goal inside the arc (+2)',               affectsScore: true,  isPositive: true,  scorePoints: 2, unit: 'count', positions: [] },
          threePointerMade:{ label: '3PT Made',         description: 'Field goal from beyond the arc (+3)',          affectsScore: true,  isPositive: true,  scorePoints: 3, unit: 'count', positions: [] },
          freeThrowMissed: { label: 'FT Missed',        description: 'Free throw attempt missed',                    affectsScore: false, isPositive: false, unit: 'count', positions: [] },
          twoPointerMissed:{ label: '2PT Missed',       description: 'Field goal attempt inside the arc missed',     affectsScore: false, isPositive: false, unit: 'count', positions: [] },
          threePointerMissed:{ label: '3PT Missed',     description: 'Field goal attempt from beyond the arc missed', affectsScore: false, isPositive: false, unit: 'count', positions: [] },
          fieldGoalPct:    { label: 'Field Goal %',     description: 'Percentage of field goals made',               affectsScore: false, isPositive: true,  unit: 'percent', positions: [] },
          threePtPct:      { label: '3-Point %',        description: 'Percentage of 3-point shots made',             affectsScore: false, isPositive: true,  unit: 'percent', positions: [] },
        },
      },
      rebound: {
        label: 'Rebound',
        icon: '🔃',
        metrics: {
          offensiveRebound:{ label: 'Offensive Rebound', description: 'Rebound on own offensive possession',         affectsScore: false, isPositive: true,  unit: 'count', positions: [] },
          defensiveRebound:{ label: 'Defensive Rebound', description: 'Rebound after opponent miss',                 affectsScore: false, isPositive: true,  unit: 'count', positions: [] },
        },
      },
      defense: {
        label: 'Defense',
        icon: '🛡️',
        metrics: {
          steal:           { label: 'Steal',            description: 'Taking possession from the opponent',           affectsScore: false, isPositive: true,  unit: 'count', positions: [] },
          blockShot:       { label: 'Block',            description: 'Blocking an opponent shot attempt',             affectsScore: false, isPositive: true,  unit: 'count', positions: [] },
        },
      },
      playmaking: {
        label: 'Playmaking',
        icon: '🎯',
        metrics: {
          assist:          { label: 'Assist',           description: 'Pass that directly leads to a basket',         affectsScore: false, isPositive: true,  unit: 'count', positions: [] },
          turnover:        { label: 'Turnover',         description: 'Losing possession to the opponent',            affectsScore: false, isPositive: false, unit: 'count', positions: [] },
        },
      },
      signature: {
        label: 'Signature',
        icon: '🔥',
        metrics: {
          dunk:            { label: 'Dunk',             description: 'Slam dunk — high-impact 2-point score',        affectsScore: true,  isPositive: true,  scorePoints: 2, unit: 'count', positions: ['Center','Power Forward','Small Forward'], legacy: true },
          andOne:          { label: 'And-1',            description: 'Scores while being fouled — earns a free throw', affectsScore: true, isPositive: true,  scorePoints: 2, unit: 'count', positions: [], legacy: true },
          fastBreakPoint:  { label: 'Fast Break Point', description: 'Point scored in transition off a steal or rebound', affectsScore: true, isPositive: true, scorePoints: 2, unit: 'count', positions: [], legacy: true },
          alleyOop:        { label: 'Alley-Oop',        description: 'Catch-and-finish from a lob pass',             affectsScore: true,  isPositive: true,  scorePoints: 2, unit: 'count', positions: ['Center','Power Forward','Small Forward'], legacy: true },
        },
      },
      discipline: {
        label: 'Discipline',
        icon: '⚠️',
        metrics: {
          foul:            { label: 'Foul',             description: 'Personal or technical foul',                   affectsScore: false, isPositive: false, unit: 'count', positions: [] },
        },
      },
      team: {
        label: 'Team',
        icon: '👥',
        metrics: {
          communication:   { label: 'Communication',   description: 'Team communication rating (1-5)',               affectsScore: false, isPositive: true,  unit: 'rating', positions: [] },
        },
      },
    },
  },

  // ── BADMINTON SINGLES ──────────────────────────────────────────────────────
  badminton: {
    positions: ['Singles Player'],
    quickMetrics: ['smashWinner', 'netKill', 'dropWinner', 'otherWinner', 'serviceAce', 'unforcedError', 'forcedError', 'serviceFault'],
    positionMetrics: {},
    categories: {
      offense: {
        label: 'Offense',
        icon: '🏸',
        metrics: {
          smashWinner:     { label: 'Smash Winner',     description: 'Smash that directly wins the rally — power stat', affectsScore: true,  isPositive: true,  unit: 'count', positions: [] },
          dropWinner:      { label: 'Drop Winner',      description: 'Deceptive drop shot winning the rally',         affectsScore: true,  isPositive: true,  unit: 'count', positions: [] },
          netKill:         { label: 'Net Kill',         description: 'Aggressive tap/kill at the net',                affectsScore: true,  isPositive: true,  unit: 'count', positions: [] },
          otherWinner:     { label: 'Other Winner',     description: 'Any other shot that wins the rally outright',   affectsScore: true,  isPositive: true,  unit: 'count', positions: [] },
          crossCourtWinner:{ label: 'Cross-Court Winner', description: 'Angled winner showing court coverage',        affectsScore: true,  isPositive: true,  unit: 'count', positions: [], legacy: true },
          serviceAce:      { label: 'Service Ace',      description: 'Serve winning the rally outright',              affectsScore: true,  isPositive: true,  unit: 'count', positions: [] },
          clear:           { label: 'Clear',            description: 'Deep baseline shot building rally position',    affectsScore: false, isPositive: true,  unit: 'count', positions: [] },
          smash:           { label: 'Smash (Any)',       description: 'Smash attempt (winner + non-winner combined)',  affectsScore: false, isPositive: true,  unit: 'count', positions: [] },
          dropShot:        { label: 'Drop Shot',        description: 'Soft net shot (any outcome)',                   affectsScore: false, isPositive: true,  unit: 'count', positions: [] },
          netShot:         { label: 'Net Shot',         description: 'Delicate net play (any outcome)',               affectsScore: false, isPositive: true,  unit: 'count', positions: [] },
        },
      },
      discipline: {
        label: 'Discipline',
        icon: '⚠️',
        metrics: {
          serviceFault:    { label: 'Service Fault',    description: 'Illegal or missed serve — opponent earns a point', affectsScore: false, isPositive: false, unit: 'count', positions: [], awardsOpponentPoint: true },
          unforcedError:   { label: 'Unforced Error',   description: 'Error without opponent pressure — opponent earns a point', affectsScore: false, isPositive: false, unit: 'count', positions: [], awardsOpponentPoint: true },
          forcedError:     { label: 'Forced Error',     description: 'Error caused by opponent pressure — opponent earns a point', affectsScore: false, isPositive: false, unit: 'count', positions: [], awardsOpponentPoint: true },
          error:           { label: 'Error',            description: 'Any error (net, out, etc.) — opponent earns a point', affectsScore: false, isPositive: false, unit: 'count', positions: [], awardsOpponentPoint: true, legacy: true },
          foul:            { label: 'Foul',             description: 'Rule violation — opponent earns a point',      affectsScore: false, isPositive: false, unit: 'count', positions: [], awardsOpponentPoint: true, legacy: true },
          sideOut:         { label: 'Side Out',         description: 'Lost the rally, possession changes — opponent earns a point', affectsScore: false, isPositive: false, unit: 'count', positions: [], awardsOpponentPoint: true, legacy: true },
        },
      },
    },
  },

  // ── BADMINTON DOUBLES (extends singles + team metrics) ────────────────────
  badminton_doubles: {
    positions: ['Player 1', 'Player 2'],
    quickMetrics: ['smashWinner', 'netKill', 'dropWinner', 'otherWinner', 'serviceAce', 'unforcedError', 'forcedError', 'serviceFault'],
    positionMetrics: {},
    categories: {
      offense: {
        label: 'Offense',
        icon: '🏸',
        metrics: {
          smashWinner:     { label: 'Smash Winner',     description: 'Smash that directly wins the rally',           affectsScore: true,  isPositive: true,  unit: 'count', positions: [] },
          dropWinner:      { label: 'Drop Winner',      description: 'Deceptive drop winning the rally',             affectsScore: true,  isPositive: true,  unit: 'count', positions: [] },
          netKill:         { label: 'Net Kill',         description: 'Aggressive tap/kill at the net',               affectsScore: true,  isPositive: true,  unit: 'count', positions: [] },
          otherWinner:     { label: 'Other Winner',     description: 'Any other shot that wins the rally outright',  affectsScore: true,  isPositive: true,  unit: 'count', positions: [] },
          crossCourtWinner:{ label: 'Cross-Court Winner', description: 'Angled winner showing court coverage',       affectsScore: true,  isPositive: true,  unit: 'count', positions: [], legacy: true },
          serviceAce:      { label: 'Service Ace',      description: 'Serve winning the rally outright',             affectsScore: true,  isPositive: true,  unit: 'count', positions: [] },
          clear:           { label: 'Clear',            description: 'Deep baseline shot building rally position',   affectsScore: false, isPositive: true,  unit: 'count', positions: [] },
          smash:           { label: 'Smash (Any)',       description: 'Smash attempt (winner + non-winner)',          affectsScore: false, isPositive: true,  unit: 'count', positions: [] },
          dropShot:        { label: 'Drop Shot',        description: 'Soft net shot (any outcome)',                  affectsScore: false, isPositive: true,  unit: 'count', positions: [] },
          netShot:         { label: 'Net Shot',         description: 'Delicate net play (any outcome)',              affectsScore: false, isPositive: true,  unit: 'count', positions: [] },
        },
      },
      discipline: {
        label: 'Discipline',
        icon: '⚠️',
        metrics: {
          serviceFault:    { label: 'Service Fault',    description: 'Illegal or missed serve — opponent earns a point', affectsScore: false, isPositive: false, unit: 'count', positions: [], awardsOpponentPoint: true },
          unforcedError:   { label: 'Unforced Error',   description: 'Error without opponent pressure — opponent earns a point', affectsScore: false, isPositive: false, unit: 'count', positions: [], awardsOpponentPoint: true },
          forcedError:     { label: 'Forced Error',     description: 'Error caused by opponent pressure — opponent earns a point', affectsScore: false, isPositive: false, unit: 'count', positions: [], awardsOpponentPoint: true },
          error:           { label: 'Error',            description: 'Any error (net, out, etc.) — opponent earns a point', affectsScore: false, isPositive: false, unit: 'count', positions: [], awardsOpponentPoint: true, legacy: true },
          foul:            { label: 'Foul',             description: 'Rule violation — opponent earns a point',      affectsScore: false, isPositive: false, unit: 'count', positions: [], awardsOpponentPoint: true, legacy: true },
          sideOut:         { label: 'Side Out',         description: 'Lost the rally, possession changes — opponent earns a point', affectsScore: false, isPositive: false, unit: 'count', positions: [], awardsOpponentPoint: true, legacy: true },
          rotationError:   { label: 'Rotation Error',   description: 'Incorrect court positioning — detailed tracking stat', affectsScore: false, isPositive: false, unit: 'count', positions: [] },
        },
      },
      team: {
        label: 'Team',
        icon: '👥',
        metrics: {
          communication:   { label: 'Communication',   description: 'Partner communication rating (1-5)',            affectsScore: false, isPositive: true,  unit: 'rating', positions: [] },
        },
      },
    },
  },

  // ── TENNIS SINGLES ─────────────────────────────────────────────────────────
  tennis: {
    positions: ['Singles Player'],
    quickMetrics: ['ace', 'winner', 'doubleFault', 'unforcedError', 'forcedError'],
    positionMetrics: {},
    categories: {
      serve: {
        label: 'Serve',
        icon: '🎾',
        metrics: {
          ace:             { label: 'Ace',              description: 'Serve unreturnable by opponent',               affectsScore: true,  isPositive: true,  unit: 'count', positions: [] },
          doubleFault:     { label: 'Double Fault',     description: 'Two consecutive faults — opponent wins the point', affectsScore: false, isPositive: false, unit: 'count', positions: [], awardsOpponentPoint: true },
        },
      },
      rally: {
        label: 'Rally',
        icon: '🏃',
        metrics: {
          winner:          { label: 'Winner',           description: 'Shot winning a rally outright',                affectsScore: true,  isPositive: true,  unit: 'count', positions: [],
                             typeField: 'winnerType', types: { volley: { label: 'Volley' } } },
          unforcedError:   { label: 'Unforced Error',   description: 'Error without opponent pressure — opponent wins the point', affectsScore: false, isPositive: false, unit: 'count', positions: [], awardsOpponentPoint: true },
          forcedError:     { label: 'Forced Error',     description: 'Error caused by opponent pressure — opponent wins the point', affectsScore: false, isPositive: false, unit: 'count', positions: [], awardsOpponentPoint: true },
        },
      },
      net: {
        label: 'Net',
        icon: '🔀',
        metrics: {
          volley:          { label: 'Volley',           description: 'Winning shot at the net',                      affectsScore: false, isPositive: true,  unit: 'count', positions: [], legacy: true },
        },
      },
      discipline: {
        label: 'Discipline',
        icon: '⚠️',
        metrics: {
          foul:            { label: 'Foul',             description: 'Code violation or unsporting conduct',         affectsScore: false, isPositive: false, unit: 'count', positions: [], legacy: true },
        },
      },
    },
  },

  // ── TENNIS DOUBLES (extends singles + team) ───────────────────────────────
  tennis_doubles: {
    positions: ['Player 1', 'Player 2'],
    quickMetrics: ['ace', 'winner', 'doubleFault', 'unforcedError', 'forcedError', 'positioningError'],
    positionMetrics: {},
    categories: {
      serve: {
        label: 'Serve',
        icon: '🎾',
        metrics: {
          ace:             { label: 'Ace',              description: 'Serve unreturnable by opponent',               affectsScore: true,  isPositive: true,  unit: 'count', positions: [] },
          doubleFault:     { label: 'Double Fault',     description: 'Two consecutive faults — opponent wins the point', affectsScore: false, isPositive: false, unit: 'count', positions: [], awardsOpponentPoint: true },
        },
      },
      rally: {
        label: 'Rally',
        icon: '🏃',
        metrics: {
          winner:          { label: 'Winner',           description: 'Shot winning a rally outright',                affectsScore: true,  isPositive: true,  unit: 'count', positions: [],
                             typeField: 'winnerType', types: { volley: { label: 'Volley' } } },
          unforcedError:   { label: 'Unforced Error',   description: 'Error without opponent pressure — opponent wins the point', affectsScore: false, isPositive: false, unit: 'count', positions: [], awardsOpponentPoint: true },
          forcedError:     { label: 'Forced Error',     description: 'Error caused by opponent pressure — opponent wins the point', affectsScore: false, isPositive: false, unit: 'count', positions: [], awardsOpponentPoint: true },
        },
      },
      net: {
        label: 'Net',
        icon: '🔀',
        metrics: {
          volley:          { label: 'Volley',           description: 'Winning shot at the net',                      affectsScore: false, isPositive: true,  unit: 'count', positions: [], legacy: true },
        },
      },
      discipline: {
        label: 'Discipline',
        icon: '⚠️',
        metrics: {
          foul:            { label: 'Foul',             description: 'Code violation or unsporting conduct',         affectsScore: false, isPositive: false, unit: 'count', positions: [], legacy: true },
          positioningError:{ label: 'Positioning Error', description: 'Incorrect court positioning between partners', affectsScore: false, isPositive: false, unit: 'count', positions: [] },
        },
      },
      team: {
        label: 'Team',
        icon: '👥',
        metrics: {
          communication:   { label: 'Communication',   description: 'Partner communication rating (1-5)',            affectsScore: false, isPositive: true,  unit: 'rating', positions: [] },
        },
      },
    },
  },

  // ── FOOTBALL ──────────────────────────────────────────────────────────────
  football: {
    positions: ['Goalkeeper', 'Defender', 'Midfielder', 'Forward'],
    quickMetrics: ['goal', 'ownGoal', 'assist', 'shotOnTarget', 'save', 'tackle', 'interception', 'foul', 'yellowCard', 'redCard'],
    positionMetrics: {
      Goalkeeper: ['save', 'fouls', 'yellowCard', 'redCard', 'communication'],
      Defender:   ['tackle', 'interception', 'fouls', 'yellowCard', 'redCard', 'communication', 'passAccuracy'],
    },
    categories: {
      attack: {
        label: 'Attack',
        icon: '⚽',
        metrics: {
          goal:            { label: 'Goal',             description: 'Ball crosses the goal line',                   affectsScore: true,  isPositive: true,  unit: 'count', positions: ['Forward','Midfielder','Defender'],
                             typeField: 'goalType',
                             types: { header: { label: 'Header' }, penalty: { label: 'Penalty', positions: ['Forward','Midfielder'] }, outsideBox: { label: 'Outside Box', positions: ['Forward','Midfielder'] } } },
          ownGoal:         { label: 'Own Goal',         description: 'Puts the ball into their own net — opponent scores', affectsScore: false, isPositive: false, unit: 'count', positions: [], awardsOpponentPoint: true },
          assist:          { label: 'Assist',           description: 'Final pass before a goal',                    affectsScore: false, isPositive: true,  unit: 'count', positions: ['Forward','Midfielder'] },
          shotOnTarget:    { label: 'Shot on Target',   description: 'Shot requiring a save or scoring',             affectsScore: false, isPositive: true,  unit: 'count', positions: ['Forward','Midfielder'] },
        },
      },
      signature: {
        label: 'Signature',
        icon: '🎯',
        metrics: {
          headerGoal:      { label: 'Header Goal',      description: 'Goal scored with the head — aerial dominance', affectsScore: true,  isPositive: true,  unit: 'count', positions: ['Forward','Midfielder','Defender'], legacy: true },
          outsideBoxGoal:  { label: 'Outside-Box Goal', description: 'Long-range goal from outside the penalty area', affectsScore: true, isPositive: true,  unit: 'count', positions: ['Forward','Midfielder'], legacy: true },
          bigChanceCreated:{ label: 'Big Chance Created', description: 'Key pass creating a high-probability scoring chance', affectsScore: false, isPositive: true, unit: 'count', positions: ['Forward','Midfielder'] },
          successfulTakeOn:{ label: 'Successful Take-On', description: 'Dribble past an opponent',                  affectsScore: false, isPositive: true,  unit: 'count', positions: ['Forward','Midfielder'] },
          penaltyGoal:     { label: 'Penalty Goal',     description: 'Goal scored from the penalty spot',           affectsScore: true,  isPositive: true,  unit: 'count', positions: ['Forward','Midfielder'], legacy: true },
        },
      },
      defense: {
        label: 'Defense',
        icon: '🛡️',
        metrics: {
          tackle:          { label: 'Tackle',           description: 'Winning the ball from an opponent',           affectsScore: false, isPositive: true,  unit: 'count', positions: ['Defender','Midfielder'] },
          interception:    { label: 'Interception',     description: 'Cutting out an opponent pass',                 affectsScore: false, isPositive: true,  unit: 'count', positions: ['Defender','Midfielder'] },
          save:            { label: 'Save',             description: 'Goalkeeper stopping a shot on target',         affectsScore: false, isPositive: true,  unit: 'count', positions: ['Goalkeeper'] },
        },
      },
      passing: {
        label: 'Passing',
        icon: '🎯',
        metrics: {
          passAccuracy:    { label: 'Pass Accuracy %',  description: 'Percentage of passes reaching a teammate',    affectsScore: false, isPositive: true,  unit: 'percent', positions: [] },
        },
      },
      discipline: {
        label: 'Discipline',
        icon: '⚠️',
        metrics: {
          foul:            { label: 'Foul',             description: 'Foul on an opponent',                         affectsScore: false, isPositive: false, unit: 'count', positions: [] },
          yellowCard:      { label: 'Yellow Card',      description: 'Caution issued by referee',                   affectsScore: false, isPositive: false, unit: 'count', positions: [] },
          redCard:         { label: 'Red Card',         description: 'Dismissal from the game',                     affectsScore: false, isPositive: false, unit: 'count', positions: [] },
        },
      },
      team: {
        label: 'Team',
        icon: '👥',
        metrics: {
          communication:   { label: 'Communication',   description: 'Team communication rating (1-5)',               affectsScore: false, isPositive: true,  unit: 'rating', positions: [] },
        },
      },
    },
  },

  // ── TABLE TENNIS ──────────────────────────────────────────────────────────
  table_tennis: {
    positions: ['Singles Player'],
    quickMetrics: ['forehandLoopWinner', 'flickWinner', 'thirdBallAttack', 'smashWinner', 'blockWinner', 'serviceAce', 'error', 'serviceError', 'foul'],
    positionMetrics: {},
    categories: {
      offense: {
        label: 'Offense',
        icon: '🏓',
        metrics: {
          forehandLoopWinner: { label: 'Forehand Loop',   description: 'Forehand topspin loop winner — signature attack', affectsScore: true,  isPositive: true,  unit: 'count', positions: [] },
          flickWinner:        { label: 'Flick Winner',    description: 'Attacking the serve directly with a flick',    affectsScore: true,  isPositive: true,  unit: 'count', positions: [] },
          thirdBallAttack:    { label: '3rd Ball Attack', description: 'Winning point immediately after your own serve', affectsScore: true, isPositive: true,  unit: 'count', positions: [] },
          smashWinner:        { label: 'Smash Winner',    description: 'Power smash winning the point',                affectsScore: true,  isPositive: true,  unit: 'count', positions: [] },
          blockWinner:        { label: 'Block Winner',    description: 'Effective counter-block winning the point',    affectsScore: true,  isPositive: true,  unit: 'count', positions: [] },
          serviceAce:         { label: 'Service Ace',     description: 'Serve the opponent cannot return at all',      affectsScore: true,  isPositive: true,  unit: 'count', positions: [] },
        },
      },
      clutch: {
        label: 'Clutch',
        icon: '⚡',
        metrics: {
          // Winning at deuce follows from the score; it is not a kind of point
          deuceWin:           { label: 'Deuce Win',       description: 'Wins a set when the score is 10-10 (deuce)',   affectsScore: true,  isPositive: true,  unit: 'count', positions: [], legacy: true },
          setPoint:           { label: 'Set Point Won',   description: 'Wins the deciding point of a set',            affectsScore: false, isPositive: true,  unit: 'count', positions: [] },
        },
      },
      serve: {
        label: 'Serve',
        icon: '🔄',
        metrics: {
          firstServeWin:      { label: '1st Serve Win',   description: 'Point won directly from first serve',          affectsScore: false, isPositive: true,  unit: 'count', positions: [] },
          serveAndAttack:     { label: 'Serve & Attack',  description: '3rd-ball attack off own serve',                affectsScore: false, isPositive: true,  unit: 'count', positions: [] },
        },
      },
      discipline: {
        label: 'Discipline',
        icon: '⚠️',
        metrics: {
          error:              { label: 'Error',           description: 'Any error (off the table, into net) — opponent earns a point', affectsScore: false, isPositive: false, unit: 'count', positions: [], awardsOpponentPoint: true },
          foul:               { label: 'Foul',            description: 'Rule violation — opponent earns a point',     affectsScore: false, isPositive: false, unit: 'count', positions: [], awardsOpponentPoint: true },
          sideOut:            { label: 'Side Out',        description: 'Lost the rally, serve passes to opponent — opponent earns a point', affectsScore: false, isPositive: false, unit: 'count', positions: [], awardsOpponentPoint: true, legacy: true },
          unforcedError:      { label: 'Unforced Error',  description: 'Error without opponent pressure — detailed tracking stat', affectsScore: false, isPositive: false, unit: 'count', positions: [] },
          serviceError:       { label: 'Service Error',   description: 'Failed serve — opponent earns a point',        affectsScore: false, isPositive: false, unit: 'count', positions: [], awardsOpponentPoint: true },
        },
      },
    },
  },
};

/**
 * Resolve any sport name ("Soccer", "Badminton Singles", "Table-Tennis", "Beach
 * Volleyball", …) to its SPORTS_METRICS key, or null (LS-B15). The sport is
 * resolved with getSportConfigKey — the same resolver the boards and rules use —
 * so every surface agrees on the sport; "doubles" in the name picks the doubles
 * catalogue for racket sports.
 */
export const resolveMetricsKey = (sportName) => {
  const raw = String(sportName || '').toLowerCase().trim();
  if (!raw) return null;
  if (Object.prototype.hasOwnProperty.call(SPORTS_METRICS, raw)) return raw;
  const doubles = /doubles/.test(raw);
  switch (getSportConfigKey(raw)) {
    case 'volleyball': return 'volleyball';
    case 'basketball': return 'basketball';
    case 'football': return 'football';
    case 'badminton': return doubles ? 'badminton_doubles' : 'badminton';
    case 'tennis': return doubles ? 'tennis_doubles' : 'tennis';
    case 'tabletennis': return 'table_tennis';
    default: return null;
  }
};

/**
 * Can this metric be tapped on the live board? Percentages and ratings
 * (fieldGoalPct, attackSuccessPct, passAccuracy, communication, …) are derived
 * from the counted events, never recorded as taps (AS-B08).
 */
export const isTappableMetric = (metric) => !!metric && (!metric.unit || metric.unit === 'count');

/**
 * The optional types of a metric (kill → Tip / Placement / Setter Dump, goal →
 * Header / Penalty / Outside Box) this player may record, as [{ key, label }].
 * A type limited to positions is kept when the player's position is unknown.
 */
export const getMetricTypes = (metric, player = null) => {
  const position = player?.position;
  return Object.entries(metric?.types || {})
    .filter(([, t]) => !position || !t.positions?.length || t.positions.includes(position))
    .map(([key, t]) => ({ key, label: t.label }));
};

/** The metric recorded for one of its types: same key and points, typed label. */
export const withMetricType = (metric, typeKey) => {
  const type = metric?.types?.[typeKey];
  if (!type) return metric;
  return { ...metric, label: `${metric.label} (${type.label})`, typeKey, typeLabel: type.label };
};

/**
 * The action recorded for a shared (two-player) volleyball block: both blockers
 * are credited a Block Assist and the team wins one point (AS-B08).
 */
export const SHARED_BLOCK_METRIC = {
  label: 'Block (Assist)',
  description: 'Shared block — both blockers are credited an assist',
  affectsScore: true,
  scorePoints: 1,
  isPositive: true,
  unit: 'count',
  positions: [],
};

/**
 * Return the metrics config for a sport, with an optional player position filter.
 * Each category only includes metrics that apply to the given position.
 */
export const getMetricsForSport = (sportName, playerPosition = null) => {
  const key = resolveMetricsKey(sportName);
  const config = key ? SPORTS_METRICS[key] : null;
  if (!config) return null;

  if (!playerPosition) return config;

  // Filter each category to position-permitted metrics only
  const filteredCategories = {};
  for (const [catKey, cat] of Object.entries(config.categories)) {
    const filteredMetrics = {};
    for (const [mKey, m] of Object.entries(cat.metrics)) {
      if (!m.positions || m.positions.length === 0 || m.positions.includes(playerPosition)) {
        filteredMetrics[mKey] = m;
      }
    }
    if (Object.keys(filteredMetrics).length > 0) {
      filteredCategories[catKey] = { ...cat, metrics: filteredMetrics };
    }
  }
  return { ...config, categories: filteredCategories };
};

/**
 * Returns true if the sport has advanced metrics configured.
 */
export const sportHasAdvancedScoring = (sportName) => {
  return resolveMetricsKey(sportName) !== null;
};
